import { useCallback, useEffect } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useValidateEmail } from '@consumer-frontend/nc-api';
import {
  emailAddressPattern,
  emailFormatErrorMessage,
  initialAlertBannerProps,
  isDefined,
  somethingWentWrongAlertBannerProps,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { NcBasicInfoPageLegal } from './NcBasicInfoPageLegal';
import { useApplyRouting } from '../../routing';
import {
  alertBannerAtom,
  doesPasswordExistAtom,
  isEmailApplyEligibleAtom,
  isReturningCustomerFlowAtom,
  registrationDataAtom,
  unableToContinueReasonAtom,
} from '../../store/atoms';
import { NcAlertBanner } from '../NcAlertBanner';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcInput } from '../NcInput';
// import { NcOauthButtons } from '../NcOauthButtons';
// import { NcTextDivider } from '../NcTextDivider';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/apply/basic-info-page.scss';

type BasicInfoFormValuesT = {
  email: string;
  firstName: string;
  lastName: string;
};

export const NcBasicInfoPage = () => {
  const { handleBack, handleContinue } = useApplyRouting();
  const {
    error,
    isMutating,
    validateEmail,
  } = useValidateEmail();
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const setIsReturningCustomerFlow = useSetAtom(isReturningCustomerFlowAtom);
  const setDoesPasswordExist = useSetAtom(doesPasswordExistAtom);
  const setIsEmailApplyEligible = useSetAtom(isEmailApplyEligibleAtom);
  const setUnableToContinueReason = useSetAtom(unableToContinueReasonAtom);
  const registrationData = useAtomValue(registrationDataAtom);

  useEffect(() => {
    if (error) {
      setAlertBanner(somethingWentWrongAlertBannerProps);
    }
  }, [error, setAlertBanner]);

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<BasicInfoFormValuesT>({
    defaultValues: {
      email: registrationData.email || '',
      firstName: registrationData.firstName || '',
      lastName: registrationData.lastName || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const emailValidations = {
    pattern: {
      message: emailFormatErrorMessage,
      value: emailAddressPattern,
    },
    required: emailFormatErrorMessage,
  };

  const onSubmit = useCallback<SubmitHandler<BasicInfoFormValuesT>>(
    async (formData) => {
      // if this errors out, we need to show an error banner instead of the usual 500 page
      const response = await validateEmail({ email: formData.email });

      if (!isDefined(response)) return;

      const { emailApplyEligible, secured } = response;

      // An ineligible email means the customer already has an account; the unable-to-continue
      // page reads this reason to show the "already have an account" header.
      if (!emailApplyEligible) {
        setUnableToContinueReason('existingEmail');
      }

      setIsEmailApplyEligible(emailApplyEligible ?? false);

      setIsReturningCustomerFlow(secured);

      setDoesPasswordExist(secured ?? false);

      await handleContinue(formData);

      setAlertBanner(initialAlertBannerProps);
    },
    [
      handleContinue,
      setAlertBanner,
      setDoesPasswordExist,
      setIsEmailApplyEligible,
      setIsReturningCustomerFlow,
      setUnableToContinueReason,
      validateEmail,
    ],
  );

  return (
    <div className="nc-basic-info-page">
      <Container hasBoxShadow>
        <h1 className="h3">Basic Information</h1>

        {error && <NcAlertBanner />}

        {/*
          Placeholder for OAuth buttons component, added here in order to complete the visual styling of the page.
          The eventual implementation should use the same structure and classNames to leverage existing CSS.

          <NcOauthButtons />

          <NcTextDivider text="Or" />
        */}

        <NcForm onSubmit={handleSubmit(onSubmit)}>
          <NcInput
            {...register(
              'firstName',
              { required: 'Enter your first name.' },
            )}
            autoComplete="given-name"
            errorMessage={errors.firstName?.message}
            id="firstName"
            label="First Name"
            type="text"
          />

          <NcInput
            {...register(
              'lastName',
              { required: 'Enter your last name.' },
            )}
            autoComplete="family-name"
            errorMessage={errors.lastName?.message}
            id="lastName"
            label="Last Name"
            type="text"
          />

          <NcInput
            {...register(
              'email',
              emailValidations,
            )}
            autoComplete="email"
            errorMessage={errors.email?.message}
            id="email"
            label="Email"
            type="email"
          />

          <Fieldset className="cta-buttons">
            <NcButton
              loading={isMutating || isSubmitting}
              type="submit"
              variant="primary"
            >
              Continue
            </NcButton>

            <NcButton
              disabled={isMutating || isSubmitting}
              onClick={handleBack}
              type="button"
              variant="outline-primary"
            >
              Back
            </NcButton>
          </Fieldset>
        </NcForm>
      </Container>

      <NcBasicInfoPageLegal />
    </div>
  );
};
